import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, MessageSquare, Zap, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

// Admin CMS tab — edits the small set of publicly-visible copy that changes
// most often (hero tagline, contact details, an announcement banner,
// timeline reveal). Everything is stored under `app_settings.content` and
// falls back to code defaults when unset. Nothing else on the site depends
// on this tab; leave it untouched to keep the compiled-in defaults.
const AdminCMSTab = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await ax.get(`${API}/admin/content-settings`);
        setContent(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load content.');
      }
    })();
  }, []);

  const update = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const { data } = await ax.put(`${API}/admin/content-settings`, content);
      setContent(data);
      toast.success('Content updated. Public pages will reflect changes on next reload.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const runReminders = async () => {
    if (!window.confirm('Send reminder emails NOW to any nominators/jurors who qualify?')) return;
    try {
      const { data } = await ax.post(`${API}/admin/reminders/run`);
      toast.success(`Reminders dispatched — drafts: ${data.drafts}, deadline: ${data.deadline}, jurors: ${data.jurors}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder run failed.');
    }
  };

  if (!content) return <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div>;

  return (
    <div data-testid="admin-cms-tab" className="max-w-3xl space-y-6">
      <p className="text-[var(--ink-muted)] text-sm">Edit high-frequency content across the public site. Changes appear on the next page load — no deploy needed.</p>

      {/* Announcement banner */}
      <Section icon={Zap} title="Announcement Banner" hint="Optional highlighted strip shown at the very top of every public page. Leave empty to hide.">
        <textarea value={content.announcement_banner || ''} onChange={(e) => update('announcement_banner', e.target.value)}
          data-testid="cms-announcement" rows={2} maxLength={200}
          placeholder="Example: 5 days left to submit — nominations close 6 September 2026"
          className="w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
        <div className="text-[10px] text-[var(--ink-muted)] mt-1 text-right">{(content.announcement_banner || '').length}/200</div>
      </Section>

      {/* Hero tagline */}
      <Section icon={MessageSquare} title="Hero Tagline" hint="Italic line beneath the main headline on the Home page.">
        <input value={content.hero_tagline || ''} onChange={(e) => update('hero_tagline', e.target.value)}
          data-testid="cms-hero-tagline" maxLength={200}
          className="w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
      </Section>

      {/* Contact */}
      <Section icon={MessageSquare} title="Contact Details" hint="Email, phone, WhatsApp number used in the footer, Contact page, and portal help card.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Labelled label="Email">
            <input type="email" value={content.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)}
              data-testid="cms-contact-email"
              className="w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
          </Labelled>
          <Labelled label="Phone">
            <input value={content.contact_phone || ''} onChange={(e) => update('contact_phone', e.target.value)}
              data-testid="cms-contact-phone"
              className="w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
          </Labelled>
          <Labelled label="WhatsApp (E.164)">
            <input value={content.contact_whatsapp || ''} onChange={(e) => update('contact_whatsapp', e.target.value)}
              data-testid="cms-contact-whatsapp" placeholder="+919717025198"
              className="w-full bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
          </Labelled>
        </div>
      </Section>

      {/* Timeline reveal */}
      <Section icon={Calendar} title="Full Timeline Reveal" hint="When enabled, Home page 'Key Dates' will show the full timeline (currently only the deadline is displayed).">
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!content.reveal_full_timeline}
            onChange={(e) => update('reveal_full_timeline', e.target.checked)}
            data-testid="cms-reveal-timeline"
            className="w-4 h-4" />
          <span className="text-[13px] text-[var(--ink)]">Show full Key Dates timeline on Home</span>
        </label>
      </Section>

      <div className="flex flex-wrap gap-3 pt-2">
        <button onClick={save} disabled={saving} data-testid="cms-save"
          className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-6 py-3 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
          <Save size={13} /> {saving ? 'Saving…' : 'Save All Changes'}
        </button>
        <button onClick={runReminders} data-testid="cms-run-reminders"
          className="inline-flex items-center gap-2 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white px-6 py-3 text-[10px] tracking-[0.28em] uppercase transition-colors">
          <Zap size={13} /> Send Reminders Now
        </button>
      </div>
      <p className="text-[10.5px] text-[var(--ink-muted)] italic max-w-xl">
        Reminders are auto-dispatched daily at 09:00 IST — this button just forces the same job to run immediately. It won&rsquo;t double-send anyone.
      </p>
    </div>
  );
};

const Section = ({ icon: Icon, title, hint, children }) => (
  <div className="border border-[var(--gold-faint)] bg-white p-5">
    <div className="flex items-start gap-3 mb-3">
      <span className="w-8 h-8 border border-[var(--gold-faint)] flex items-center justify-center flex-shrink-0"><Icon size={14} className="text-[var(--gold)]" /></span>
      <div>
        <h3 className="font-serif text-base text-[var(--ink)]">{title}</h3>
        <p className="text-[11.5px] text-[var(--ink-muted)] leading-relaxed mt-0.5">{hint}</p>
      </div>
    </div>
    {children}
  </div>
);

const Labelled = ({ label, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--gold)] mb-1">{label}</div>
    {children}
  </div>
);

export default AdminCMSTab;
